import { Injectable } from '@angular/core';
import { ShellClientService } from '../shell-communication/shell-client.service';
import { CollarsBucketDto, ListCollarsBuckets } from '../shell-communication/collars-operations';
import {
   AnalysisComboStateDto, ListPendingAnalysisBlocks, ListPendingAnalysisBlocksReply
} from '../shell-communication/analysis-session-operations';

/**
 * The Collars-side reader of the analysis design lock (CR-20260930-001, M1): is a combo under a live
 * Portfolio Hedging Analysis session? The Shell REFUSES every design write on such a combo, but a
 * refused command carries no text across the WCF fault boundary — so the panel asks first, with a
 * query, and tells the trader WHY before sending anything. Reads only; the Shell stays the authority.
 */
@Injectable()
export class CollarsAnalysisLockService {

   /** The reason shown wherever a design action is stopped on a locked combo. */
   static readonly LOCK_REASON =
      'This combo has changes in the Portfolio Hedging Analysis panel. ' +
      'Design changes are locked until they are exported and acquired, revoked and undone, or the analysis is reset; ' +
      'its exported orders are applied from Apply After State, or cleared with Clear After State.';

   constructor(private readonly _shellClient: ShellClientService) { }

   /**
    * The analysis state of every accessible combo that has one (locked and/or carrying pending blocks),
    * narrowed to the given combos when any are given (M2: the panel's earmarks + Clear After State read this).
    */
   async comboStates(comboIds: string[] = []): Promise<AnalysisComboStateDto[]> {
      const reply = await this._shellClient.processQuery<ListPendingAnalysisBlocksReply>(new ListPendingAnalysisBlocks(comboIds));
      return ((reply && reply.combos) || []).map(c => ({ ...c, pendingBlocks: c.pendingBlocks || [] }));
   }

   /** The ids of the locked combos among the given ones (all accessible combos when none are given). */
   async lockedComboIds(comboIds: string[] = []): Promise<Set<string>> {
      const locked = new Set<string>();
      (await this.comboStates(comboIds)).forEach(c => { if (c.isLocked) { locked.add(c.comboId); } });
      return locked;
   }

   /**
    * The lock reason when any of the given buckets (any level) lies under a locked combo, else null.
    * A portfolio counts as locked when any combo under it is. Buckets are resolved from `buckets` when
    * given, else fetched.
    */
   async reasonFor(bucketIds: string[], buckets?: CollarsBucketDto[]): Promise<string | null> {
      const ids = (bucketIds || []).filter(id => !!id);
      if (ids.length === 0) { return null; }
      const all = buckets || (await this._shellClient.processQuery<CollarsBucketDto[]>(new ListCollarsBuckets())) || [];
      const byId: { [id: string]: CollarsBucketDto } = {};
      all.forEach(b => { byId[b.bucketId] = b; });

      const combos = new Set<string>();
      ids.forEach(id => CollarsAnalysisLockService.combosOf(id, byId, all).forEach(c => combos.add(c)));
      if (combos.size === 0) { return null; }

      const locked = await this.lockedComboIds(Array.from(combos));
      return locked.size > 0 ? CollarsAnalysisLockService.LOCK_REASON : null;
   }

   /** The combo(s) a bucket belongs to: itself (combo), its parent (group), every child combo (portfolio). */
   static combosOf(bucketId: string, byId: { [id: string]: CollarsBucketDto }, all: CollarsBucketDto[]): string[] {
      const b = byId[bucketId];
      if (!b) { return []; }
      switch (b.level) {
         case 1: return [b.bucketId];
         case 2: return b.parentBucketId ? [b.parentBucketId] : [];
         default: return all.filter(x => x.level === 1 && x.parentBucketId === b.bucketId).map(x => x.bucketId);
      }
   }
}
